'use client';

import {
  MouseEvent,
  TouchEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Image from 'next/image';
import clsx from 'clsx';
import { icons } from '@/assets/icons';
import styles from './CircularGallery.module.scss';

export interface CircularGalleryItem {
  id: string;
  name: string;
  position: string;
  description: string;
  imageSrc: string;
}

interface CircularGalleryProps {
  items: CircularGalleryItem[];
  className?: string;
}

// Physics and visual constants
// Strength of the spring (0-1). Higher is stiffer.
const SPRING_STRENGTH = 0.1;
const DRAG_DAMPING = 0.8; // More damping for wheel
const SNAP_DAMPING = 0.5;
const DRAG_SENSITIVITY = 0.05; // Degrees per pixel for desktop
const TOUCH_SENSITIVITY = 0.2; // Degrees per pixel for touch

export default function CircularGallery({
  items,
  className,
}: CircularGalleryProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLUListElement>(null);
  const slideRefs = useRef<(HTMLLIElement | null)[]>([]);

  const [activeIndex, setActiveIndex] = useState(0);
  const activeIndexRef = useRef(0);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  const state = useRef({
    rotation: 0, // Current rotation in degrees (replaces scrollX)
    targetRotation: 0, // Target for snapping
    velocity: 0,
    isDragging: false,
    isSnapping: false,
    startX: 0,
    startY: 0,
    lastX: 0,
    angleStep: 25,
    trackWidth: 0,
    initialized: false,
    hasDragged: false,
  });

  const requestRef = useRef<number | null>(null);
  const isAnimating = useRef(false);

  // Modulo function that handles negative numbers correctly
  const mod = (n: number, m: number) => ((n % m) + m) % m;

  const update = useCallback(() => {
    const cardsEl = cardsRef.current;
    if (!cardsEl || !state.current.initialized) {
      isAnimating.current = false;
      return;
    }

    const s = state.current;
    let shouldContinue = false;

    // Physics Loop
    if (s.isDragging) {
      shouldContinue = true;
    } else if (s.isSnapping) {
      const dist = s.targetRotation - s.rotation;
      const force = dist * SPRING_STRENGTH;
      s.velocity += force;
      s.velocity *= SNAP_DAMPING;
      s.rotation += s.velocity;

      if (Math.abs(dist) < 0.1 && Math.abs(s.velocity) < 0.05) {
        s.rotation = s.targetRotation;
        s.velocity = 0;
        s.isSnapping = false;
      } else {
        shouldContinue = true;
      }
    } else {
      // Momentum
      if (Math.abs(s.velocity) > 0.01) {
        s.rotation += s.velocity;
        s.velocity *= DRAG_DAMPING;
        shouldContinue = true;
      } else {
        s.velocity = 0;
      }
    }

    // Render if we are active
    // Calculate Active Index
    const halfTrack = s.trackWidth / 2;
    let closestDist = Infinity;
    let closestIndex = -1;

    items.forEach((_, i) => {
      const slide = slideRefs.current[i];
      if (!slide) return;

      const itemBaseAngle = i * s.angleStep;
      let angle = itemBaseAngle + s.rotation;

      while (angle < -halfTrack) angle += s.trackWidth;
      while (angle > halfTrack) angle -= s.trackWidth;

      // Apply transform directly instead of CSS variable for performance
      slide.style.transform = `rotate(${angle}deg)`;

      const dist = Math.abs(angle);
      if (dist < closestDist) {
        closestDist = dist;
        closestIndex = i;
      }
    });

    if (closestIndex !== -1 && closestIndex !== activeIndexRef.current) {
      setActiveIndex(closestIndex);
    }

    if (shouldContinue) {
      requestRef.current = requestAnimationFrame(update);
    } else {
      isAnimating.current = false;
    }
  }, [items]);

  const startLoop = useCallback(() => {
    if (!isAnimating.current) {
      isAnimating.current = true;
      requestRef.current = requestAnimationFrame(update);
    }
  }, [update]);

  // --- Measurement & Resize ---
  useEffect(() => {
    if (items.length === 0) return;

    const measure = () => {
      if (!cardsRef.current) return;

      // Read --angle-step from CSS
      const computedStyle = getComputedStyle(cardsRef.current);
      const angleStepStr = computedStyle
        .getPropertyValue('--angle-step')
        .trim();
      const angleStepVal = parseFloat(angleStepStr) || 25; // fallback

      state.current.angleStep = angleStepVal;

      state.current.trackWidth = items.length * angleStepVal;

      if (!state.current.initialized) {
        state.current.rotation = 0;
        state.current.targetRotation = 0;
        setActiveIndex(0);
        state.current.initialized = true;
      } else {
        // Snap to current active to prevent drift on resize
        const target = -(activeIndexRef.current * angleStepVal);
        state.current.targetRotation = target;
      }
    };

    measure();
    const observer = new ResizeObserver(measure);
    if (wrapperRef.current) observer.observe(wrapperRef.current);
    window.addEventListener('resize', measure);

    startLoop();

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
      isAnimating.current = false;
    };
  }, [items.length, update, startLoop]);

  // --- Helpers ---
  const snapTo = (index: number) => {
    const s = state.current;
    const count = items.length;
    const normalizedIndex = mod(index, count);

    const targetBaseAngle = normalizedIndex * s.angleStep;

    let currentItemAngle = targetBaseAngle + s.rotation;

    const half = s.trackWidth / 2;
    while (currentItemAngle < -half) currentItemAngle += s.trackWidth;
    while (currentItemAngle > half) currentItemAngle -= s.trackWidth;

    s.targetRotation = s.rotation - currentItemAngle;
    s.isSnapping = true;
    startLoop();
  };

  // --- Event Handlers ---
  const handleDragStart = (startX: number, startY: number) => {
    state.current.isDragging = true;
    state.current.isSnapping = false;
    state.current.startX = startX;
    state.current.startY = startY;
    state.current.lastX = startX;
    state.current.velocity = 0;
    state.current.hasDragged = false;
    if (cardsRef.current) {
      cardsRef.current.classList.add(styles.isDragging);
    }
    startLoop();
  };

  const handleDragMove = (
    currentX: number,
    currentY: number,
    isTouch = false
  ) => {
    if (!state.current.isDragging) return;

    // Vertical scroll check for touch
    if (isTouch && !state.current.hasDragged) {
      const deltaX = Math.abs(currentX - state.current.startX);
      const deltaY = Math.abs(currentY - state.current.startY);
      // If moving more vertically than horizontally, release drag to allow scroll
      if (deltaY > deltaX && deltaY > 10) {
        state.current.isDragging = false;
        if (cardsRef.current) {
          cardsRef.current.classList.remove(styles.isDragging);
        }
        return;
      }
    }

    const deltaX = currentX - state.current.lastX;
    state.current.lastX = currentX;

    if (Math.abs(currentX - state.current.startX) > 5) {
      state.current.hasDragged = true;
    }

    // Drag logic: moving mouse LEFT (negative delta) should rotate RIGHT (positive angle)?

    const sensitivity = isTouch ? TOUCH_SENSITIVITY : DRAG_SENSITIVITY;
    state.current.rotation += deltaX * sensitivity;

    if (!isAnimating.current) startLoop();
  };

  const handleDragEnd = () => {
    state.current.isDragging = false;
    if (cardsRef.current) {
      cardsRef.current.classList.remove(styles.isDragging);
    }

    // Momentum / Snap
    // Simple snap to nearest
    const s = state.current;

    // Find closest item index
    const rawStepCount = s.rotation / s.angleStep;
    const targetStepCount = Math.round(rawStepCount);

    s.targetRotation = targetStepCount * s.angleStep;
    s.isSnapping = true;
  };

  const onMouseDown = (e: MouseEvent) => handleDragStart(e.clientX, e.clientY);
  const onMouseMove = (e: MouseEvent) =>
    handleDragMove(e.clientX, e.clientY, false);
  const onMouseUp = () => handleDragEnd();
  const onMouseLeave = () => handleDragEnd();

  const onTouchStart = (e: TouchEvent) =>
    handleDragStart(e.touches[0].clientX, e.touches[0].clientY);
  const onTouchMove = (e: TouchEvent) =>
    handleDragMove(e.touches[0].clientX, e.touches[0].clientY, true);
  const onTouchEnd = () => handleDragEnd();
  const onTouchCancel = () => handleDragEnd();

  return (
    <div className={clsx(styles.wrapper, className)} ref={wrapperRef}>
      <ul
        ref={cardsRef}
        className={styles.cards}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchCancel}
      >
        {items.map((item, index) => {
          const isActive = index === activeIndex;

          return (
            <li
              key={item.id}
              className={clsx(isActive && styles.active)}
              ref={(el) => {
                slideRefs.current[index] = el;
              }}
              onClick={() => {
                if (!state.current.hasDragged) {
                  snapTo(index);
                }
              }}
            >
              <div className={styles.cardLabel}>
                <Image
                  className={styles.cardImage}
                  src={item.imageSrc}
                  alt={item.name}
                  width={300}
                  height={300}
                  draggable={false}
                />
              </div>
              <div className={styles.cardContent}>
                <h3 className={styles.name}>{item.name}</h3>
                <h4 className={styles.position}>{item.position}</h4>
                <p className={styles.description}>{item.description}</p>
              </div>
            </li>
          );
        })}
      </ul>

      <div className={styles.controls}>
        <button
          type="button"
          className={styles.navButton}
          aria-label="Previous member"
          onClick={() => snapTo(activeIndex - 1)}
        >
          <svg className={clsx(styles.navIcon, styles.prevIcon)}>
            <use href={`${icons.src}#icon-arrow_rounded`} />
          </svg>
        </button>
        <button
          type="button"
          className={styles.navButton}
          aria-label="Next member"
          onClick={() => snapTo(activeIndex + 1)}
        >
          <svg className={clsx(styles.navIcon, styles.nextIcon)}>
            <use href={`${icons.src}#icon-arrow_rounded`} />
          </svg>
        </button>
      </div>
    </div>
  );
}
